import { useState, useEffect, useCallback, useRef } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { filterAtom, topicsAtom, mediaAtom, paginationAtom, mediaFetchKeyAtom, topicsLoadedAtom } from '../state/filterAtom';
import { getMedia, getTopics } from '../api/media';

// Generate a random seed per page load for consistent paginated random ordering
const sessionSeed = Math.random() * 2 - 1;

export const useMedia = () => {
  const [filters, setFilters] = useRecoilState(filterAtom);
  const setTopics = useSetRecoilState(topicsAtom);
  const [media, setMedia] = useRecoilState(mediaAtom);
  const [pagination, setPagination] = useRecoilState(paginationAtom);
  const [fetchKey, setFetchKey] = useRecoilState(mediaFetchKeyAtom);
  const [topicsLoaded, setTopicsLoaded] = useRecoilState(topicsLoadedAtom);
  const [loading, setLoading] = useState(media.length === 0);
  const [error, setError] = useState(null);

  const filterKey = `${filters.search}|${filters.topic}|${filters.type}|${filters.page}|${filters.limit}`;

  useEffect(() => {
    if (fetchKey === filterKey && media.length > 0) {
      setLoading(false);
      return;
    }

    if (media.length === 0) setLoading(true);

    let cancelled = false;
    const doFetch = async () => {
      setError(null);
      try {
        const params = {
          search: filters.search || undefined,
          topic: filters.topic !== 'all' ? filters.topic : undefined,
          type: filters.type !== 'all' ? filters.type : undefined,
          page: filters.page,
          limit: filters.limit,
          seed: sessionSeed
        };
        const response = await getMedia(params);
        if (!cancelled) {
          setMedia(response.media);
          setPagination(response.pagination);
          setFetchKey(filterKey);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch media:', err);
          setError('Failed to load media');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    doFetch();
    return () => { cancelled = true; };
  }, [filterKey, fetchKey, media.length, filters, setMedia, setPagination, setFetchKey]);

  useEffect(() => {
    if (topicsLoaded) return;

    let cancelled = false;
    const doFetch = async () => {
      try {
        const topics = await getTopics();
        if (!cancelled) {
          setTopics(topics);
          setTopicsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch topics:', err);
      }
    };

    doFetch();
    return () => { cancelled = true; };
  }, [topicsLoaded, setTopics, setTopicsLoaded]);

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page !== undefined ? newFilters.page : 1
    }));
  }, [setFilters]);

  const setPage = useCallback((page) => {
    setFilters((prev) => ({ ...prev, page }));
  }, [setFilters]);

  const refresh = useCallback(() => {
    setFetchKey(null);
    setTopicsLoaded(false);
  }, [setFetchKey, setTopicsLoaded]);

  return {
    media,
    pagination,
    loading,
    error,
    filters,
    updateFilters,
    setPage,
    refresh
  };
};
